import { store } from '@/store';
import { setUser } from '@/store/slices/auth';

export const formatNumberToK = (number: string | number) => {
  const num = typeof number === 'string' ? parseFloat(number) : number;

  // Return original if not a valid number
  if (isNaN(num) || num === 0) return '0';

  // Handle negative numbers
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  let formatted = '';

  if (absNum >= 1000000000000) {
    // Trillions
    formatted = (absNum / 1000000000000).toFixed(absNum % 1000000000000 === 0 ? 0 : 1) + 'T';
  } else if (absNum >= 1000000000) {
    // Billions
    formatted = (absNum / 1000000000).toFixed(absNum % 1000000000 === 0 ? 0 : 1) + 'B';
  } else if (absNum >= 1000000) {
    // Millions
    formatted = (absNum / 1000000).toFixed(absNum % 1000000 === 0 ? 0 : 1) + 'M';
  } else if (absNum >= 1000) {
    // Thousands
    formatted = (absNum / 1000).toFixed(absNum % 1000 === 0 ? 0 : 1) + 'K';
  } else {
    // Less than 1000
    formatted = absNum.toString();
  }

  return isNegative ? '-' + formatted : formatted;
};

export const setUserInStore = (userToJson: any): void => {
  const { displayName, email, phoneNumber, photoURL, uid } = userToJson;

  const nameParts = displayName.split(' ');
  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(' ');

  // store user in Redux
  store.dispatch(setUser({ firstName, lastName, name: displayName, email, phoneNumber, photoURL, id: uid }));
};

/**
 * Returns the full currency name and symbol for a given currency code
 * @param currencyCode - The ISO currency code (e.g., 'USD', 'EUR')
 * @returns The formatted currency name and symbol (e.g., 'US Dollar ($)')
 */
export const getCurrencyInfo = (currencyCode: string): string => {
  const currencies: Record<string, { name: string; symbol: string }> = {
    USD: { name: 'US Dollar', symbol: '$' },
    EUR: { name: 'Euro', symbol: '€' },
    GBP: { name: 'British Pound', symbol: '£' },
    JPY: { name: 'Japanese Yen', symbol: '¥' },
    CAD: { name: 'Canadian Dollar', symbol: 'CA$' },
    AUD: { name: 'Australian Dollar', symbol: 'A$' },
    CHF: { name: 'Swiss Franc', symbol: 'CHF' },
    CNY: { name: 'Chinese Yuan', symbol: '¥' },
    INR: { name: 'Indian Rupee', symbol: '₹' },
    BRL: { name: 'Brazilian Real', symbol: 'R$' }
  };

  // Normalize the currency code (uppercase and trim)
  const normalizedCode = currencyCode.trim().toUpperCase();

  // Return formatted string if currency exists, otherwise return the code
  if (currencies[normalizedCode]) {
    const { name, symbol } = currencies[normalizedCode];
    return `${name} (${symbol})`;
  }

  return normalizedCode; // Return the code itself if not found
};

/**
 * Converts a date string from YYYY-MM-DD format to MM/DD/YYYY format
 * @param dateString - Date string in YYYY-MM-DD format (e.g., '2025-04-02')
 * @returns Date string in MM/DD/YYYY format (e.g., '04/02/2025')
 */
export const formatDate = (dateString: string): string => {
  // Return empty string if dateString is empty or invalid
  if (!dateString) return '';

  try {
    // Create a new Date object from the input string
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Get month, day, and year
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    // Return formatted date string (MM/DD/YYYY)
    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// Calculate remaining days before data deletion
export const calculateRemainingDays = (dateString: string): number => {
  console.log('dateString', dateString);
  if (!dateString) return 0;

  const createdDate = new Date(dateString);
  // Add 30 days to the created date
  const targetDate = new Date(createdDate);
  targetDate.setDate(targetDate.getDate() + 30);

  const today = new Date();
  const timeDiff = targetDate.getTime() - today.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

  return Math.max(0, daysDiff);
};
